import {Alert, Button, Col, Image, Row, Spin} from 'antd'
import {LeftOutlined, RightOutlined} from '@ant-design/icons'
import {useEffect, useState, useRef, useCallback} from 'react'
import moment from 'moment'
import {GET} from 'helpers/api'
import {useSelector} from 'react-redux'
import {calculateEndDate} from 'helpers/dates'
import ThermalImage from '@components/thermal-image'

/**
 * Displays up to 4 temperature records with blend and thermal images, with navigation support.
 * Fetches data from images_temperaturesview API and image URLs from images API.
 * Uses the same period calculation as ThermalRow - based on clicked temperature data point.
 * @param {Object} temperature - Temperature object with timestamp property (from clicked data point)
 * @param {Object} interval - Interval object with value property (minutes)
 * @param {number} tgid - Target ID for API calls
 */
export const TemperatureImageGallery = ({temperature, interval, tgid}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [imagesData, setImagesData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [imageUrls, setImageUrls] = useState({}) // Store image URLs by timestamp
  const [loadingImages, setLoadingImages] = useState({}) // Track which images are loading
  const imageUrlCache = useRef({}) // Cache for image URLs by timestamp
  const token = useSelector(state => state.auth.token)

  // Fetch data from images_temperaturesview API
  // Calculate period the same way ThermalRow does - from temperature and interval
  useEffect(() => {
    const fetchImagesData = async () => {
      if (!temperature || !interval || !tgid) {
        setImagesData(null)
        setCurrentIndex(0)
        return
      }

      setLoading(true)
      setError(null)

      let url // declare url in the same scope as try/catch
      try {
        // Calculate period exactly like ThermalRow does
        const start = new Date(temperature.timestamp)
        const end = calculateEndDate(start, interval.value)

        const request = GET(token)
        url =
          process.env.REACT_APP_BACKEND_URL +
          `/images_temperaturesview?tgid=${tgid}&i_type=blend&start=${start.toISOString()}&end=${end.toISOString()}&limit=100&page=1&order=ASC`

        const response = await fetch(url, request)

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          console.error('API Error:', {
            status: response.status,
            statusText: response.statusText,
            errorData,
            url
          })
          setError(`Failed to fetch images data: ${response.status} ${response.statusText}`)
          setImagesData(null)
          return
        }

        const data = await response.json()

        // Handle response structure - could be array or object with images_temperaturesview key
        const imagesArray = Array.isArray(data) ? data : data.images_temperaturesview

        if (imagesArray && Array.isArray(imagesArray)) {
          if (imagesArray.length === 0) {
            console.log('No images found for the selected period:', {
              tgid,
              start: start.toISOString(),
              end: end.toISOString(),
              url
            })
            setError('No images found for the selected period.')
            setImagesData([])
          } else {
            setImagesData(imagesArray)
            setCurrentIndex(0) // Reset to first image when data loads
            setError(null)
          }
        } else {
          console.error('Unexpected response structure:', {data, url})
          setError('Unexpected response format from API')
          setImagesData(null)
        }
      } catch (err) {
        console.error('Error fetching images_temperaturesview:', err, {url})
        setError(`Error loading images: ${err.message}`)
        setImagesData(null)
      } finally {
        setLoading(false)
      }
    }

    fetchImagesData()
  }, [temperature, interval, tgid, token])

  // Fetch image URL for a given timestamp with caching
  const fetchImageUrl = useCallback(
    async timestamp => {
      // Check cache first
      const cacheKey = timestamp
      if (imageUrlCache.current[cacheKey]) {
        return imageUrlCache.current[cacheKey]
      }

      // Mark as loading
      setLoadingImages(prev => {
        // Check if already loading
        if (prev[cacheKey]) {
          return prev // Already loading, don't update
        }
        return {...prev, [cacheKey]: true}
      })

      try {
        const timestampDate = timestamp instanceof Date ? timestamp : new Date(timestamp)
        const request = GET(token)
        const url =
          process.env.REACT_APP_BACKEND_URL +
          `/images?tgid=${tgid}&type=blend&timestamp=${timestampDate.toISOString()}&limit=1`

        const response = await fetch(url, request)
        const data = await response.json()

        if (response.ok && data.images && data.images.length > 0) {
          const imageUrl = data.images[0].url
          // Store in cache
          imageUrlCache.current[cacheKey] = imageUrl
          // Update state
          setImageUrls(prev => ({...prev, [cacheKey]: imageUrl}))
          return imageUrl
        } else {
          // No image found
          imageUrlCache.current[cacheKey] = null
          setImageUrls(prev => ({...prev, [cacheKey]: null}))
          return null
        }
      } catch (err) {
        console.error('Error fetching image URL:', err)
        imageUrlCache.current[cacheKey] = null
        setImageUrls(prev => ({...prev, [cacheKey]: null}))
        return null
      } finally {
        setLoadingImages(prev => {
          const newState = {...prev}
          delete newState[cacheKey]
          return newState
        })
      }
    },
    [tgid, token]
  )

  // Fetch image URLs for currently displayed items
  useEffect(() => {
    if (!imagesData || imagesData.length === 0) return

    const selectedItems = imagesData.slice(currentIndex, currentIndex + 4)
    selectedItems.forEach(item => {
      const timestamp = item.itv_timestamp || item.timestamp
      if (timestamp) {
        fetchImageUrl(timestamp)
      }
    })
  }, [imagesData, currentIndex, fetchImageUrl])

  // Left navigation: Navigate to previous 4 images, restricted to period boundaries
  // Cannot navigate before the first image in the period (period.start)
  const handleLeftNavigation = () => {
    if (currentIndex === 0 || !imagesData) return // Already at the first image in period
    setCurrentIndex(Math.max(0, currentIndex - 4))
  }

  // Right navigation: Navigate to next 4 images, restricted to period boundaries
  // Cannot navigate beyond the last image in the period (period.end)
  const handleRightNavigation = () => {
    if (!imagesData) return
    const nextIndex = currentIndex + 4
    if (nextIndex < imagesData.length) {
      setCurrentIndex(nextIndex)
    }
    // If nextIndex >= imagesData.length, we're at the last image in period, do nothing
  }

  // Handle loading state
  if (loading) {
    return (
      <div style={{minHeight: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <Spin />
      </div>
    )
  }

  // Handle error state
  if (error) {
    return <Alert message={error} type="error" />
  }

  // Handle no data state
  if (!imagesData || imagesData.length === 0) {
    return <Alert message="No images found for the selected period." type="info" />
  }

  // Get the 4 items to display starting from currentIndex
  const selectedItems = imagesData.slice(currentIndex, currentIndex + 4)

  if (selectedItems.length === 0) {
    return <Alert message="No images found for the selected period." type="info" />
  }

  // Calculate edge case states for button disabling based on period boundaries
  // Left button disabled when at the first image in the period (cannot go before period.start)
  // Right button disabled when at the last image in the period (cannot go beyond period.end)
  // Since imagesData is already filtered by period from the API, navigation is automatically restricted
  const isAtBeginning = currentIndex === 0 // At first image in period
  const isAtEnd = currentIndex + 4 >= imagesData.length // At or past last image in period

  return (
    <>
      <Row justify="center" style={{marginTop: 24, marginBottom: 16}}>
        <Button.Group>
          <Button icon={<LeftOutlined />} onClick={handleLeftNavigation} disabled={isAtBeginning} />
          <Button icon={<RightOutlined />} onClick={handleRightNavigation} disabled={isAtEnd} />
        </Button.Group>
      </Row>
      <Row gutter={16}>
        {selectedItems.map((item, idx) => {
          const imageTimestamp = item.itv_timestamp || item.timestamp
          const temperatureTimestamp = item.tp_timestamp
          const cacheKey = imageTimestamp
          const blendImageUrl = imageUrls[cacheKey]
          const isLoadingImage = loadingImages[cacheKey]

          // Calculate thermal image height to match blend image height
          // ThermalImage uses: displayHeight = rows * scale (default scale=10, default rows=24)
          const thermalRows = item.ta_temperatures?.length || 24
          const thermalScale = 10 // Default scale from ThermalImage component
          const thermalHeight = thermalRows * thermalScale

          return (
            <Col xs={24} md={6} key={imageTimestamp || idx}>
              <div style={{textAlign: 'center', marginBottom: 16}}>
                {/* Blend image (top) */}
                <div>
                  {isLoadingImage ? (
                    <Spin size="small" />
                  ) : blendImageUrl ? (
                    <Image
                      src={blendImageUrl}
                      alt="Blend"
                      style={{
                        height: thermalHeight,
                        width: 'auto',
                        border: '1px solid #ccc',
                        display: 'block',
                        margin: '0 auto'
                      }}
                      preview={{
                        mask: 'Click to enlarge'
                      }}
                    />
                  ) : (
                    <div style={{width: 320, height: thermalHeight, border: '1px solid #ccc', display: 'inline-block'}}>
                      No image
                    </div>
                  )}
                </div>

                {/* Image timestamp below blend image */}
                {imageTimestamp && (
                  <div style={{marginTop: 8}}>
                    <b>{moment(imageTimestamp).format('YYYY-MM-DD HH:mm')}</b>
                  </div>
                )}

                {/* Thermal image (below blend image) */}
                <div style={{marginTop: 16}}>
                  <ThermalImage temperatures={item.ta_temperatures} minimum={item.tp_min} maximum={item.tp_max} />
                </div>

                {/* Temperature timestamp below thermal image */}
                {temperatureTimestamp && (
                  <div style={{marginTop: 8}}>
                    <b>{moment(temperatureTimestamp).format('YYYY-MM-DD HH:mm')}</b>
                  </div>
                )}
              </div>
            </Col>
          )
        })}
      </Row>
    </>
  )
}
